import {
  RELAY_REQUEST_HEADERS,
  RelayEncryptedApplicationPayload,
  RelayEndpointConfirmation,
  RelayDeliveryMiddleware,
  RelayTransportServerMessage,
  RelayWsRpcGroup,
  type RelayDeliveryClass,
  type RelayActionGrantRequest,
  type RelayJournalPersistence,
  type RelayRpcEnvelope,
  type RelayRpcEnvelopeV4,
  type RelayScope,
} from "@cmmd-forge/contracts";
import {
  authorizeRelayRpcRequest,
  canonicalRelayPayloadJson,
  relaySensitiveActionIdentity,
  relayTransportControlRequestIdentity,
  RelayProtocolFailure,
} from "@cmmd-forge/shared/relayProtocol";
import {
  createRelayPayloadBinding,
  createRelayPayloadRecipient,
  createRelayPayloadSender,
  deserializeRelayEndpointPublicKey,
  RELAY_PAYLOAD_SUITE,
  relayPayloadTicketDigestBase64Url,
  serializeRelayEndpointPublicKey,
  type RelayPayloadBinding,
  type RelayPayloadDigestProvider,
  type RelayPayloadRecipient,
  type RelayPayloadRandomSource,
  type RelayPayloadSender,
} from "@cmmd-forge/shared/relayPayloadChannel";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schedule from "effect/Schedule";
import * as Schema from "effect/Schema";
import { RpcClient, RpcMiddleware, RpcSerialization } from "effect/unstable/rpc";
import * as Socket from "effect/unstable/socket/Socket";

import {
  createMemoryRelayControlJournalStorage,
  createRelayControlJournal,
  type RelayControlJournal,
  type RelayControlJournalRecord,
} from "./relayControlJournal";
import { getWsReconnectDelayMsForRetry, WS_RECONNECT_MAX_RETRIES } from "./wsRpcProtocol";

export interface RelayControlRoute {
  readonly token: string;
  readonly relayWebSocketUrl: string;
  readonly protocolVersion: number;
  readonly runtimeId?: string;
  readonly connectionEpoch?: number;
  readonly channelId?: string;
  readonly hpkeSuite?: string;
  readonly runtimeEndpointKeyId?: string;
  readonly runtimeEndpointPublicKey?: string;
  readonly controlEndpointKeyId?: string;
  readonly controlEndpointPublicKey?: string;
  readonly controlChannelNonce?: string;
  readonly controlEndpointKeyPair?: CryptoKeyPair;
  readonly payloadDigestProvider?: RelayPayloadDigestProvider;
  readonly payloadRandomSource?: RelayPayloadRandomSource;
}

export interface RelayRpcProtocolOptions {
  readonly acquireRoute: () => Promise<RelayControlRoute>;
  readonly acquireActionGrant?: (request: RelayActionGrantRequest) => Promise<string>;
  readonly journal: RelayControlJournal;
  readonly crypto?: RelayCrypto;
  readonly maxRetries?: number;
  readonly delayForRetry?: (retryIndex: number) => number | null;
  readonly lifecycle?: RelayConnectionLifecycle;
}

export interface RelayConnectionLifecycle {
  readonly onConnecting?: () => void;
  readonly onAuthenticated?: () => void;
  readonly onDisconnected?: (details: { readonly code: number; readonly reason: string }) => void;
  readonly onError?: (details: { readonly code: string; readonly message: string }) => void;
}

export interface RelayCrypto {
  readonly randomUUID: () => string;
  readonly sha256Base64Url: (payload: string) => Promise<string>;
}

export interface RelayRouteSocketBinding {
  readonly acquireSocketUrl: () => Promise<string>;
  readonly constructSocket: (url: string | URL, protocols?: string | string[]) => WebSocket;
}

interface PendingRelayRoute {
  readonly route: RelayControlRoute;
  readonly expires: ReturnType<typeof setTimeout>;
}

const decodeRelayServerMessage = Schema.decodeUnknownOption(RelayTransportServerMessage);
const decodeRelayEndpointConfirmation = Schema.decodeUnknownSync(RelayEndpointConfirmation);
const decodeRelayEncryptedApplicationPayload = Schema.decodeUnknownSync(
  RelayEncryptedApplicationPayload,
);
const CONTROL_SUPPORTED_CAPABILITIES = [
  "rpc.read",
  "rpc.mutate",
  "cursor.resume",
  "terminal.stream",
  "browser.stream",
  "bulk.transfer",
  "envelope.validation",
  "payload.e2e",
] as const;

interface RelayControlContext {
  readonly runtimeId: string;
  readonly connectionEpoch: number;
  readonly protocolVersion: number;
  readonly actor: RelayRpcEnvelope["actor"];
  readonly scopes: ReadonlyArray<RelayScope>;
  readonly channelId?: string;
  readonly channelTicketDigest?: string;
  readonly runtimeEndpointKeyId?: string;
  readonly controlEndpointKeyId?: string;
}

interface RelayPayloadControlChannel {
  readonly controlToRuntimeBinding: RelayPayloadBinding;
  readonly runtimeToControlBinding: RelayPayloadBinding;
  readonly sender: RelayPayloadSender;
  readonly runtimePublicKey: CryptoKey;
  recipient: RelayPayloadRecipient | null;
  phase: "awaiting-runtime-hello" | "awaiting-runtime-confirm" | "ready";
}

interface RpcFrameIdentity {
  readonly messageType: "request" | "acknowledgement";
  readonly requestId: string;
  readonly correlationId: string;
  readonly method: string;
  readonly requiredScope: RelayScope | null;
  readonly deliveryClass: RelayDeliveryClass;
  readonly journalPersistence: RelayJournalPersistence;
}

type RelayServerMessage = typeof RelayTransportServerMessage.Type;
type RelayAuthenticatedMessage = Extract<
  RelayServerMessage,
  { readonly type: "relay.authenticated" }
>;

function headerValue(headers: unknown, name: string): string | null {
  if (!Array.isArray(headers)) return null;
  for (const entry of headers) {
    if (
      Array.isArray(entry) &&
      entry.length === 2 &&
      entry[0] === name &&
      typeof entry[1] === "string"
    ) {
      return entry[1];
    }
  }
  return null;
}

function parseRpcFrameIdentity(data: string, scopes: ReadonlyArray<RelayScope>): RpcFrameIdentity {
  const parsed = JSON.parse(data) as Record<string, unknown>;
  const transportControl = relayTransportControlRequestIdentity(parsed);
  if (transportControl !== null) {
    return { messageType: "request", ...transportControl };
  }
  const request = effectRpcRequestIdentity(parsed, scopes);
  if (request !== null) return request;
  const acknowledgement = effectRpcAcknowledgementIdentity(parsed);
  if (acknowledgement !== null) return acknowledgement;
  throw new Error("Forge relay protocol v2 requires a recognized Effect RPC frame.");
}

function effectRpcRequestIdentity(
  frame: Readonly<Record<string, unknown>>,
  scopes: ReadonlyArray<RelayScope>,
): RpcFrameIdentity | null {
  if (frame._tag !== "Request") return null;
  if (typeof frame.id !== "string" || frame.id.length === 0) return null;
  if (typeof frame.tag !== "string" || frame.tag.length === 0) return null;
  const policy = authorizeRelayRpcRequest({
    method: frame.tag,
    payload: frame.payload,
    grantedScopes: scopes,
  });
  return {
    messageType: "request",
    requestId: frame.id,
    correlationId: headerValue(frame.headers, RELAY_REQUEST_HEADERS.requestId) ?? frame.id,
    method: frame.tag,
    ...policy,
  };
}

function effectRpcAcknowledgementIdentity(
  frame: Readonly<Record<string, unknown>>,
): RpcFrameIdentity | null {
  if (frame._tag !== "Ack" && frame._tag !== "Interrupt") return null;
  if (typeof frame.requestId !== "string" || frame.requestId.length === 0) return null;
  return {
    messageType: "acknowledgement",
    requestId: frame.requestId,
    correlationId: frame.requestId,
    method: `@effect/rpc/${frame._tag}`,
    requiredScope: null,
    deliveryClass: "read",
    journalPersistence: "none",
  };
}

function createRelayErrorEvent(code: string, message: string): Event {
  const event = new Event("error");
  Object.defineProperties(event, {
    error: { value: new Error(message), enumerable: true },
    message: { value: `${code}: ${message}`, enumerable: true },
  });
  return event;
}

interface RelayFailureCandidate {
  readonly code?: unknown;
  readonly kind?: unknown;
  readonly retryable?: unknown;
  readonly status?: unknown;
}

const RELAY_API_FAILURE_KINDS = new Set(["network", "server", "auth", "permission", "client"]);
const RELAY_RETRYABLE_FAILURE_KINDS = new Set(["network", "server"]);

function relayFailureCandidate(error: unknown): RelayFailureCandidate {
  return error && typeof error === "object" ? (error as RelayFailureCandidate) : {};
}

function relayFailureCode(candidate: RelayFailureCandidate): string {
  if (typeof candidate.code === "string" && candidate.code.length > 0) return candidate.code;
  return typeof candidate.kind === "string" && RELAY_API_FAILURE_KINDS.has(candidate.kind)
    ? "RELAY_ACTION_GRANT_UNAVAILABLE"
    : "MALFORMED_FRAME";
}

function relayFailureIsRetryable(candidate: RelayFailureCandidate): boolean {
  if (candidate.retryable === true) return true;
  if (typeof candidate.kind === "string" && RELAY_RETRYABLE_FAILURE_KINDS.has(candidate.kind)) {
    return true;
  }
  const status = typeof candidate.status === "number" ? candidate.status : null;
  return status === 429 || (status !== null && status >= 500);
}

function relayFrameFailure(error: unknown): {
  readonly code: string;
  readonly message: string;
  readonly retryable: boolean;
} {
  const candidate = relayFailureCandidate(error);
  return {
    code: relayFailureCode(candidate),
    message: error instanceof Error ? error.message : "Forge relay frame encoding failed.",
    retryable: relayFailureIsRetryable(candidate),
  };
}

function copyEventProperties(event: Event, properties: readonly string[]): Event {
  const forwarded = new Event(event.type);
  const source = event as unknown as Readonly<Record<string, unknown>>;
  for (const property of properties) {
    if (source[property] !== undefined) {
      Object.defineProperty(forwarded, property, {
        value: source[property],
        enumerable: true,
      });
    }
  }
  return forwarded;
}

function isRelayDeliveryHeaderEntry(entry: unknown): boolean {
  if (!Array.isArray(entry) || entry.length !== 2 || typeof entry[0] !== "string") return false;
  return Object.values(RELAY_REQUEST_HEADERS).includes(
    entry[0] as (typeof RELAY_REQUEST_HEADERS)[keyof typeof RELAY_REQUEST_HEADERS],
  );
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

function relayFailureCauseCode(cause: unknown): string | null {
  if (!Array.isArray(cause)) return null;
  for (const entry of cause) {
    const failure = asRecord(entry);
    if (failure?._tag !== "Fail") continue;
    const code = asRecord(failure.error)?.code;
    if (typeof code === "string" && code.length > 0) return code;
  }
  return null;
}

function relayExitFailureCode(frame: Readonly<Record<string, unknown>>): string | null {
  const taggedExit = asRecord(frame.exit);
  if (taggedExit === null) return "RELAY_RPC_RESPONSE_INVALID";
  if (taggedExit._tag === "Success") return null;
  if (taggedExit._tag !== "Failure" || !Array.isArray(taggedExit.cause)) {
    return "RELAY_RPC_RESPONSE_INVALID";
  }
  return relayFailureCauseCode(taggedExit.cause) ?? "RELAY_RPC_FAILED";
}

function decodeRelayServerFrame(data: unknown): RelayServerMessage | null {
  if (typeof data !== "string") return null;
  try {
    const decoded = decodeRelayServerMessage(JSON.parse(data));
    return decoded._tag === "Some" ? decoded.value : null;
  } catch {
    return null;
  }
}

function relayAuthenticationContext(
  message: RelayAuthenticatedMessage,
  routeProtocolVersion: number,
): RelayControlContext | null | undefined {
  const protocolIsValid =
    message.role === "control" &&
    message.protocolVersion === routeProtocolVersion &&
    message.protocolVersion >= 1;
  if (!protocolIsValid) return undefined;
  if (message.protocolVersion === 1) return null;
  if (message.actor === undefined || message.scopes === undefined) return undefined;
  return {
    runtimeId: message.runtimeId,
    connectionEpoch: message.connectionEpoch,
    protocolVersion: message.protocolVersion,
    actor: message.actor,
    scopes: message.scopes,
  };
}

function recordRelayTerminalOutcome(
  data: unknown,
  context: RelayControlContext,
  journal: RelayControlJournal,
): void {
  if (typeof data !== "string") return;
  try {
    const frame = asRecord(JSON.parse(data));
    if (frame?._tag !== "Exit" || typeof frame.requestId !== "string") return;
    const failureCode = relayExitFailureCode(frame);
    if (failureCode === null) {
      journal.complete(context.runtimeId, context.actor.sessionId, frame.requestId);
      return;
    }
    journal.markFailed(context.runtimeId, context.actor.sessionId, frame.requestId, failureCode);
  } catch {
    // The RPC decoder owns malformed response handling.
  }
}

function relayEnvelopeSequence(headers: unknown): number | undefined {
  const value = headerValue(headers, RELAY_REQUEST_HEADERS.sequence);
  if (value === null) return undefined;
  const sequence = Number(value);
  return Number.isSafeInteger(sequence) && sequence >= 0 ? sequence : undefined;
}

function relayEnvelopeLifetime(headers: unknown): {
  readonly issuedAt: string;
  readonly expiresAt: string;
} {
  return {
    issuedAt: headerValue(headers, RELAY_REQUEST_HEADERS.issuedAt) ?? new Date().toISOString(),
    expiresAt:
      headerValue(headers, RELAY_REQUEST_HEADERS.expiresAt) ??
      new Date(Date.now() + 120_000).toISOString(),
  };
}

function relayEnvelopeProtocolVersion(version: number): 2 | 3 | 4 {
  if (version !== 2 && version !== 3 && version !== 4) {
    throw new RelayProtocolFailure(
      "protocol_version_mismatch",
      `Unsupported Forge relay envelope version: ${version}`,
      false,
    );
  }
  return version;
}

async function acquireSensitiveActionGrant(input: {
  readonly context: RelayControlContext;
  readonly identity: RpcFrameIdentity;
  readonly payload: unknown;
  readonly payloadHash: string;
  readonly acquire?: (request: RelayActionGrantRequest) => Promise<string>;
}): Promise<string | undefined> {
  const sensitiveAction = relaySensitiveActionIdentity({
    method: input.identity.method,
    payload: input.payload,
  });
  if (sensitiveAction === null) return undefined;
  const versionBinding = relayActionGrantVersionBinding(input.context);
  if (input.acquire === undefined) {
    throw new RelayProtocolFailure(
      "action_grant_required",
      "CMMD action-grant issuance is unavailable for this remote response.",
      false,
    );
  }
  return input.acquire({
    ...versionBinding,
    method: "orchestration.dispatchCommand",
    ...sensitiveAction,
    durableRequestId: input.identity.correlationId,
    payloadHash: input.payloadHash,
    requiredScope: "approval.respond",
  });
}

function relayActionGrantVersionBinding(context: RelayControlContext):
  | { readonly protocolVersion: 3 }
  | {
      readonly protocolVersion: 4;
      readonly channelId: string;
      readonly channelTicketDigest: string;
      readonly runtimeEndpointKeyId: string;
      readonly controlEndpointKeyId: string;
    } {
  if (context.protocolVersion === 3) return { protocolVersion: 3 };
  if (context.protocolVersion !== 4) {
    throw new RelayProtocolFailure(
      "protocol_update_required",
      "Update Forge on the target runtime before responding remotely.",
      false,
    );
  }
  return {
    protocolVersion: 4,
    channelId: requireRelayActionGrantBinding(context.channelId),
    channelTicketDigest: requireRelayActionGrantBinding(context.channelTicketDigest),
    runtimeEndpointKeyId: requireRelayActionGrantBinding(context.runtimeEndpointKeyId),
    controlEndpointKeyId: requireRelayActionGrantBinding(context.controlEndpointKeyId),
  };
}

function requireRelayActionGrantBinding(value: string | undefined): string {
  if (typeof value === "string" && value.length > 0) return value;
  throw new RelayProtocolFailure(
    "channel_ticket_binding_invalid",
    "The encrypted Forge relay channel is missing its CMMD ticket binding.",
    false,
  );
}

function optionalRelaySequence(sequence: number | undefined): { readonly sequence?: number } {
  return sequence === undefined ? {} : { sequence };
}

function optionalRelayActionGrant(actionGrant: string | undefined): {
  readonly actionGrant?: string;
} {
  return actionGrant === undefined ? {} : { actionGrant };
}

async function createRelayRpcEnvelope(input: {
  readonly data: string;
  readonly context: RelayControlContext;
  readonly crypto: RelayCrypto;
  readonly acquireActionGrant?: (request: RelayActionGrantRequest) => Promise<string>;
}): Promise<RelayRpcEnvelope> {
  const identity = parseRpcFrameIdentity(input.data, input.context.scopes);
  const parsed = JSON.parse(input.data) as {
    readonly headers?: unknown;
    readonly payload?: unknown;
  };
  const lifetime = relayEnvelopeLifetime(parsed.headers);
  const sequence = relayEnvelopeSequence(parsed.headers);
  const payloadHash = await input.crypto.sha256Base64Url(input.data);
  const actionGrant = await acquireSensitiveActionGrant({
    context: input.context,
    identity,
    payload: parsed.payload,
    payloadHash,
    ...(input.acquireActionGrant === undefined ? {} : { acquire: input.acquireActionGrant }),
  });
  const protocolVersion = relayEnvelopeProtocolVersion(input.context.protocolVersion);
  const envelope = {
    messageType: identity.messageType,
    requestId: identity.requestId,
    correlationId: identity.correlationId,
    method: identity.method,
    requiredScope: identity.requiredScope,
    deliveryClass: identity.deliveryClass,
    runtimeId: input.context.runtimeId,
    connectionEpoch: input.context.connectionEpoch,
    actor: input.context.actor,
    ...lifetime,
    payloadHash,
    ...optionalRelaySequence(sequence),
  };
  if (protocolVersion === 2) return { protocolVersion: 2, ...envelope };
  if (protocolVersion === 3) {
    return { protocolVersion: 3, ...envelope, ...optionalRelayActionGrant(actionGrant) };
  }
  return { protocolVersion: 4, ...envelope, ...optionalRelayActionGrant(actionGrant) };
}

type RelayV4ControlRoute = RelayControlRoute & {
  readonly protocolVersion: 4;
  readonly runtimeId: string;
  readonly connectionEpoch: number;
  readonly channelId: string;
  readonly hpkeSuite: typeof RELAY_PAYLOAD_SUITE;
  readonly runtimeEndpointKeyId: string;
  readonly runtimeEndpointPublicKey: string;
  readonly controlEndpointKeyId: string;
  readonly controlEndpointPublicKey: string;
  readonly controlChannelNonce: string;
  readonly controlEndpointKeyPair: CryptoKeyPair;
};

function nonEmptyRelayField(value: unknown): boolean {
  return typeof value === "string" && value.length > 0;
}

function relayV4RouteIsComplete(route: RelayControlRoute): route is RelayV4ControlRoute {
  return [
    route.protocolVersion === 4,
    nonEmptyRelayField(route.runtimeId),
    Number.isSafeInteger(route.connectionEpoch),
    Number(route.connectionEpoch) > 0,
    route.hpkeSuite === RELAY_PAYLOAD_SUITE,
    nonEmptyRelayField(route.channelId),
    nonEmptyRelayField(route.runtimeEndpointKeyId),
    nonEmptyRelayField(route.runtimeEndpointPublicKey),
    nonEmptyRelayField(route.controlEndpointKeyId),
    nonEmptyRelayField(route.controlEndpointPublicKey),
    nonEmptyRelayField(route.controlChannelNonce),
    route.controlEndpointKeyPair !== undefined,
  ].every(Boolean);
}

function requireRelayV4ControlRoute(route: RelayControlRoute): RelayV4ControlRoute {
  if (!relayV4RouteIsComplete(route)) {
    throw new RelayProtocolFailure(
      "end_to_end_encryption_required",
      "Forge relay protocol v4 requires complete endpoint encryption metadata.",
      false,
    );
  }
  return route;
}

async function createRelayPayloadControlChannel(
  route: RelayV4ControlRoute,
): Promise<RelayPayloadControlChannel> {
  const ownPublicKey = await serializeRelayEndpointPublicKey(
    route.controlEndpointKeyPair.publicKey,
  );
  if (ownPublicKey !== route.controlEndpointPublicKey) {
    throw new RelayProtocolFailure(
      "endpoint_key_invalid",
      "The control endpoint private key does not match the CMMD route.",
      false,
    );
  }
  const runtimePublicKey = await deserializeRelayEndpointPublicKey(route.runtimeEndpointPublicKey);
  const [controlToRuntimeBinding, runtimeToControlBinding] = await Promise.all([
    createRelayPayloadBinding(
      {
        exactCompactTicket: route.token,
        channelId: route.channelId,
        direction: "control-to-runtime",
        runtimeId: route.runtimeId,
        connectionEpoch: route.connectionEpoch,
      },
      route.payloadDigestProvider,
    ),
    createRelayPayloadBinding(
      {
        exactCompactTicket: route.token,
        channelId: route.channelId,
        direction: "runtime-to-control",
        runtimeId: route.runtimeId,
        connectionEpoch: route.connectionEpoch,
      },
      route.payloadDigestProvider,
    ),
  ]);
  const sender = await createRelayPayloadSender({
    binding: controlToRuntimeBinding,
    senderKey: route.controlEndpointKeyPair,
    recipientPublicKey: runtimePublicKey,
    ...(route.payloadRandomSource ? { randomSource: route.payloadRandomSource } : {}),
  });
  return {
    controlToRuntimeBinding,
    runtimeToControlBinding,
    sender,
    runtimePublicKey,
    recipient: null,
    phase: "awaiting-runtime-hello",
  };
}

type RelayEncryptedServerFrame = Extract<
  RelayServerMessage,
  { readonly type: "relay.encrypted-frame" }
>;

function encryptedFrameMatches(
  message: RelayEncryptedServerFrame,
  route: RelayV4ControlRoute,
  frameClass: "handshake" | "application",
): boolean {
  return [
    message.direction === "runtime-to-control",
    message.frameClass === frameClass,
    message.channelId === route.channelId,
  ].every(Boolean);
}

function endpointConfirmationMatches(input: {
  readonly confirmation: ReturnType<typeof decodeRelayEndpointConfirmation>;
  readonly expectedPhase: "runtime-hello" | "runtime-confirm";
  readonly route: RelayV4ControlRoute;
  readonly context: RelayControlContext;
}): boolean {
  return [
    input.confirmation.phase === input.expectedPhase,
    input.confirmation.channelId === input.route.channelId,
    input.confirmation.runtimeId === input.context.runtimeId,
    input.confirmation.connectionEpoch === input.context.connectionEpoch,
  ].every(Boolean);
}

function encryptedApplicationMatchesContext(input: {
  readonly application: ReturnType<typeof decodeRelayEncryptedApplicationPayload>;
  readonly context: RelayControlContext;
  readonly payloadHash: string;
}): boolean {
  const { envelope } = input.application;
  return [
    envelope.runtimeId === input.context.runtimeId,
    envelope.connectionEpoch === input.context.connectionEpoch,
    envelope.actor.userId === input.context.actor.userId,
    envelope.actor.deviceId === input.context.actor.deviceId,
    envelope.actor.sessionId === input.context.actor.sessionId,
    envelope.payloadHash === input.payloadHash,
  ].every(Boolean);
}

async function ensureRuntimeRelayRecipient(input: {
  readonly channel: RelayPayloadControlChannel;
  readonly route: RelayV4ControlRoute;
  readonly message: RelayEncryptedServerFrame;
}): Promise<RelayPayloadRecipient> {
  if (input.channel.recipient !== null) {
    if (input.message.encapsulatedKey !== undefined) {
      throw new RelayProtocolFailure(
        "endpoint_key_invalid",
        "The runtime attempted to replace its endpoint encapsulation.",
        false,
      );
    }
    return input.channel.recipient;
  }
  if (input.message.sequence !== 0 || !input.message.encapsulatedKey) {
    throw new RelayProtocolFailure(
      "channel_confirmation_failed",
      "The runtime handshake omitted its endpoint encapsulation.",
      false,
    );
  }
  const recipient = await createRelayPayloadRecipient({
    binding: input.channel.runtimeToControlBinding,
    recipientKey: input.route.controlEndpointKeyPair,
    senderPublicKey: input.channel.runtimePublicKey,
    encapsulatedKey: input.message.encapsulatedKey,
  });
  input.channel.recipient = recipient;
  return recipient;
}

class RelayControlWebSocket extends EventTarget {
  readonly CONNECTING = WebSocket.CONNECTING;
  readonly OPEN = WebSocket.OPEN;
  readonly CLOSING = WebSocket.CLOSING;
  readonly CLOSED = WebSocket.CLOSED;
  binaryType: "blob" | "arraybuffer" = "blob";
  onopen: ((event: Event) => void) | null = null;
  onclose: ((event: CloseEvent) => void) | null = null;
  onerror: ((event: Event) => void) | null = null;
  onmessage: ((event: MessageEvent) => void) | null = null;
  private authenticated = false;
  private context: RelayControlContext | null = null;
  private payloadChannel: RelayPayloadControlChannel | null = null;
  private sendChain = Promise.resolve();

  constructor(
    private readonly socket: WebSocket,
    private readonly route: RelayControlRoute,
    private readonly relayCrypto: RelayCrypto,
    private readonly journal: RelayControlJournal,
    private readonly lifecycle: RelayConnectionLifecycle = {},
    private readonly acquireActionGrant?: (request: RelayActionGrantRequest) => Promise<string>,
  ) {
    super();
    socket.addEventListener("open", () => void this.beginAuthentication());
    socket.addEventListener("message", (event) => void this.handleMessageSafely(event));
    socket.addEventListener("error", (event) => {
      const forwarded = copyEventProperties(event, ["error", "message"]);
      const message =
        typeof (forwarded as Event & { readonly message?: unknown }).message === "string"
          ? String((forwarded as Event & { readonly message: string }).message)
          : "Forge relay connection failed.";
      this.lifecycle.onError?.({ code: "RELAY_SOCKET_ERROR", message });
      this.emit("error", forwarded);
    });
    socket.addEventListener("close", (event) => {
      this.authenticated = false;
      this.context = null;
      this.payloadChannel = null;
      this.lifecycle.onDisconnected?.({ code: event.code, reason: event.reason });
      this.emit("close", copyEventProperties(event, ["code", "reason", "wasClean"]));
    });
  }

  get bufferedAmount(): number {
    return this.socket.bufferedAmount;
  }
  get extensions(): string {
    return this.socket.extensions;
  }
  get protocol(): string {
    return this.socket.protocol;
  }
  get readyState(): number {
    return this.authenticated ? this.socket.readyState : WebSocket.CONNECTING;
  }
  get url(): string {
    return this.route.relayWebSocketUrl;
  }

  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (!this.authenticated || typeof data !== "string") {
      throw new DOMException(
        "Forge relay RPC socket is not open for text frames.",
        "InvalidStateError",
      );
    }
    if (this.route.protocolVersion === 1) {
      this.socket.send(JSON.stringify({ type: "relay.frame", payload: data }));
      return;
    }
    const context = this.context;
    if (context === null) {
      throw new DOMException("Forge relay v2 context is unavailable.", "InvalidStateError");
    }
    const identity = parseRpcFrameIdentity(data, context.scopes);
    const preparedRecord =
      identity.messageType === "request" && identity.journalPersistence !== "none"
        ? this.journal.prepare({
            runtimeId: context.runtimeId,
            actorSessionId: context.actor.sessionId,
            transportRequestId: identity.requestId,
            ...(identity.correlationId !== identity.requestId
              ? { durableRequestId: identity.correlationId }
              : {}),
            method: identity.method,
            frame: data,
            persistence: identity.journalPersistence,
          })
        : null;
    this.enqueueFrame(data, preparedRecord);
  }

  private enqueueFrame(data: string, preparedRecord: RelayControlJournalRecord | null): void {
    this.sendChain = this.sendChain
      .then(() => this.transmitFrame(data, preparedRecord))
      .catch((error: unknown) => {
        const failure = relayFrameFailure(error);
        if (preparedRecord !== null && !failure.retryable) {
          this.journal.markFailed(
            preparedRecord.runtimeId,
            preparedRecord.actorSessionId,
            preparedRecord.transportRequestId,
            failure.code,
          );
        }
        this.lifecycle.onError?.({ code: failure.code, message: failure.message });
        this.emit("error", createRelayErrorEvent(failure.code, failure.message));
        if (this.socket.readyState < WebSocket.CLOSING) {
          this.socket.close(4400, `${failure.code}: ${failure.message}`);
        }
      });
  }

  private async transmitFrame(
    data: string,
    preparedRecord: RelayControlJournalRecord | null,
  ): Promise<void> {
    const context = this.requireReadyContext();
    const outboundData = await this.outboundFrame(data, preparedRecord);
    const envelope = await createRelayRpcEnvelope({
      data: outboundData,
      context,
      crypto: this.relayCrypto,
      ...(this.acquireActionGrant ? { acquireActionGrant: this.acquireActionGrant } : {}),
    });
    if (context.protocolVersion === 4) {
      const channel = this.requirePayloadChannel();
      const frame = await channel.sender.seal({
        frameClass: "application",
        plaintext: new TextEncoder().encode(
          JSON.stringify({ payload: outboundData, envelope: envelope as RelayRpcEnvelopeV4 }),
        ),
      });
      this.socket.send(JSON.stringify(frame));
      return;
    }
    this.socket.send(JSON.stringify({ type: "relay.frame", payload: outboundData, envelope }));
  }

  private requireReadyContext(): RelayControlContext {
    if (!this.authenticated || this.socket.readyState !== WebSocket.OPEN) {
      throw new DOMException("Forge relay RPC socket closed before send.", "InvalidStateError");
    }
    if (this.context === null) {
      throw new DOMException("Forge relay v2 context is unavailable.", "InvalidStateError");
    }
    return this.context;
  }

  private requirePayloadChannel(): RelayPayloadControlChannel {
    const channel = this.payloadChannel;
    if (channel === null || channel.phase !== "ready") {
      throw new RelayProtocolFailure(
        "channel_confirmation_failed",
        "The Forge relay endpoint channel is not confirmed.",
        false,
      );
    }
    return channel;
  }

  private async outboundFrame(
    data: string,
    preparedRecord: RelayControlJournalRecord | null,
  ): Promise<string> {
    if (preparedRecord === null) return data;
    return this.withDurableHeaders(preparedRecord, data);
  }

  private async withDurableHeaders(
    record: RelayControlJournalRecord,
    frame: string,
  ): Promise<string> {
    const attempted = this.journal.beginAttempt(
      record.runtimeId,
      record.actorSessionId,
      record.transportRequestId,
    );
    const parsed = JSON.parse(frame) as {
      readonly payload?: unknown;
      readonly headers?: unknown;
      readonly [key: string]: unknown;
    };
    const deliveryHeaders = await createRelayDeliveryHeaders({
      payload: payloadAsRuntimeDecodes(record.method, parsed.payload),
      sequence: attempted.sequence,
      requestId: attempted.durableRequestId,
      issuedAt: new Date(attempted.updatedAt),
      crypto: this.relayCrypto,
    });
    const retainedHeaders = Array.isArray(parsed.headers)
      ? parsed.headers.filter((entry) => !isRelayDeliveryHeaderEntry(entry))
      : [];
    return JSON.stringify({
      ...parsed,
      headers: [...retainedHeaders, ...Object.entries(deliveryHeaders)],
    });
  }

  close(code?: number, reason?: string): void {
    this.socket.close(code, reason);
  }

  asWebSocket(): WebSocket {
    return this as unknown as WebSocket;
  }

  private emit(type: "open" | "close" | "error" | "message", event: Event): void {
    this.dispatchEvent(event);
    const listener = this[`on${type}`];
    if (listener) listener.call(this.asWebSocket(), event as never);
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    if (this.authenticated) {
      if (this.context?.protocolVersion === 4) {
        await this.handleEncryptedApplicationMessage(event.data);
        return;
      }
      if (this.context !== null) recordRelayTerminalOutcome(event.data, this.context, this.journal);
      this.emit("message", new MessageEvent("message", { data: event.data }));
      return;
    }
    await this.handleAuthenticationMessage(event.data);
  }

  private async handleMessageSafely(event: MessageEvent): Promise<void> {
    try {
      await this.handleMessage(event);
    } catch (error) {
      const failure = relayFrameFailure(error);
      this.lifecycle.onError?.({ code: failure.code, message: failure.message });
      this.emit("error", createRelayErrorEvent(failure.code, failure.message));
      if (this.socket.readyState < WebSocket.CLOSING) {
        this.socket.close(4400, `${failure.code}: ${failure.message}`);
      }
    }
  }

  private async beginAuthentication(): Promise<void> {
    try {
      if (this.route.protocolVersion === 4) {
        this.payloadChannel = await createRelayPayloadControlChannel(
          requireRelayV4ControlRoute(this.route),
        );
      }
      this.socket.send(
        JSON.stringify({
          type: "relay.auth",
          ticket: this.route.token,
          protocolMinVersion: this.route.protocolVersion,
          protocolMaxVersion: this.route.protocolVersion,
          capabilities: CONTROL_SUPPORTED_CAPABILITIES,
          ...(this.payloadChannel
            ? { controlToRuntimeEncapsulatedKey: this.payloadChannel.sender.encapsulatedKey }
            : {}),
        }),
      );
    } catch (error) {
      const failure = relayFrameFailure(error);
      this.lifecycle.onError?.({ code: failure.code, message: failure.message });
      this.emit("error", createRelayErrorEvent(failure.code, failure.message));
      this.socket.close(4400, failure.message);
    }
  }

  private async handleAuthenticationMessage(data: unknown): Promise<void> {
    const message = decodeRelayServerFrame(data);
    if (message === null) return;
    if (message.type === "relay.encrypted-frame" && this.context?.protocolVersion === 4) {
      await this.handleEncryptedHandshakeMessage(message);
      return;
    }
    if (message.type === "relay.error") {
      this.lifecycle.onError?.({ code: message.code, message: message.message });
      this.emit("error", createRelayErrorEvent(message.code, message.message));
      this.socket.close(4403, message.message);
      return;
    }
    if (message.type !== "relay.authenticated") return;
    await this.handleAuthenticatedMessage(message);
  }

  private async handleAuthenticatedMessage(
    message: Extract<RelayServerMessage, { readonly type: "relay.authenticated" }>,
  ): Promise<void> {
    let context = relayAuthenticationContext(message, this.route.protocolVersion);
    if (context === undefined) {
      this.socket.close(4400, "Forge relay authenticated the wrong role.");
      return;
    }
    if (context !== null && context.protocolVersion === 4) {
      const route = requireRelayV4ControlRoute(this.route);
      const payloadChannel = this.payloadChannel;
      if (
        payloadChannel === null ||
        ![
          message.runtimeId === route.runtimeId,
          message.connectionEpoch === route.connectionEpoch,
        ].every(Boolean)
      ) {
        this.socket.close(4400, "Forge relay authenticated stale endpoint metadata.");
        return;
      }
      context = {
        ...context,
        channelId: route.channelId,
        channelTicketDigest: relayPayloadTicketDigestBase64Url(
          payloadChannel.controlToRuntimeBinding,
        ),
        runtimeEndpointKeyId: route.runtimeEndpointKeyId,
        controlEndpointKeyId: route.controlEndpointKeyId,
      };
      this.context = context;
      return;
    }
    await this.activateAuthenticatedRelay(context);
  }

  private async handleEncryptedHandshakeMessage(message: RelayServerMessage): Promise<void> {
    if (message.type !== "relay.encrypted-frame") return;
    const channel = this.payloadChannel;
    const context = this.context;
    const route = requireRelayV4ControlRoute(this.route);
    if (
      channel === null ||
      context === null ||
      !encryptedFrameMatches(message, route, "handshake")
    ) {
      throw new RelayProtocolFailure(
        "channel_confirmation_failed",
        "The runtime sent an invalid encrypted handshake frame.",
        false,
      );
    }
    const recipient = await ensureRuntimeRelayRecipient({ channel, route, message });
    const plaintext = await recipient.open(message);
    const confirmation = decodeRelayEndpointConfirmation(
      JSON.parse(new TextDecoder().decode(plaintext)),
    );
    const expectedPhase =
      channel.phase === "awaiting-runtime-hello" ? "runtime-hello" : "runtime-confirm";
    if (!endpointConfirmationMatches({ confirmation, expectedPhase, route, context })) {
      throw new RelayProtocolFailure(
        "channel_confirmation_failed",
        "The runtime endpoint confirmation does not match the CMMD route.",
        false,
      );
    }
    if (expectedPhase === "runtime-hello") {
      const controlConfirmation = await channel.sender.seal({
        frameClass: "handshake",
        plaintext: new TextEncoder().encode(
          JSON.stringify({
            type: "relay.endpoint-confirmation",
            protocolVersion: 4,
            phase: "control-confirm",
            channelId: route.channelId,
            runtimeId: context.runtimeId,
            connectionEpoch: context.connectionEpoch,
          }),
        ),
      });
      channel.phase = "awaiting-runtime-confirm";
      this.socket.send(JSON.stringify(controlConfirmation));
      return;
    }
    channel.phase = "ready";
    await this.activateAuthenticatedRelay(context);
  }

  private async handleEncryptedApplicationMessage(data: unknown): Promise<void> {
    const message = decodeRelayServerFrame(data);
    if (message?.type !== "relay.encrypted-frame") {
      throw new RelayProtocolFailure(
        "end_to_end_encryption_required",
        "The runtime sent plaintext after the encrypted channel was confirmed.",
        false,
      );
    }
    const channel = this.requirePayloadChannel();
    const route = requireRelayV4ControlRoute(this.route);
    if (
      !encryptedFrameMatches(message, route, "application") ||
      message.encapsulatedKey !== undefined ||
      channel.recipient === null
    ) {
      throw new RelayProtocolFailure(
        "channel_authentication_failed",
        "The runtime sent an invalid encrypted application frame.",
        false,
      );
    }
    const plaintext = await channel.recipient.open(message);
    const application = decodeRelayEncryptedApplicationPayload(
      JSON.parse(new TextDecoder().decode(plaintext)),
    );
    const context = this.requireReadyContext();
    const payloadHash = await this.relayCrypto.sha256Base64Url(application.payload);
    if (!encryptedApplicationMatchesContext({ application, context, payloadHash })) {
      throw new RelayProtocolFailure(
        "channel_authentication_failed",
        "The decrypted runtime envelope does not match the active route.",
        false,
      );
    }
    recordRelayTerminalOutcome(application.payload, context, this.journal);
    this.emit("message", new MessageEvent("message", { data: application.payload }));
  }

  private async activateAuthenticatedRelay(context: RelayControlContext | null): Promise<void> {
    this.context = context;
    this.authenticated = true;
    this.lifecycle.onAuthenticated?.();
    const replayable = context === null ? [] : this.replayForActor(context);
    for (const record of replayable) {
      if (record.frame !== null) this.enqueueFrame(record.frame, record);
    }
    if (replayable.length > 0) await this.sendChain;
    this.emit("open", new Event("open"));
  }

  private replayForActor(context: RelayControlContext): ReadonlyArray<RelayControlJournalRecord> {
    this.journal.fenceActor(context.runtimeId, context.actor.sessionId);
    return this.journal.replayable(context.runtimeId, context.actor.sessionId);
  }
}

export function createRelayControlWebSocket(
  socket: WebSocket,
  route: RelayControlRoute,
  relayCrypto: RelayCrypto = webRelayCrypto,
  journal: RelayControlJournal = createRelayControlJournal({
    storage: createMemoryRelayControlJournalStorage(),
    randomUUID: relayCrypto.randomUUID,
  }),
  lifecycle: RelayConnectionLifecycle = {},
  acquireActionGrant?: (request: RelayActionGrantRequest) => Promise<string>,
): WebSocket {
  return new RelayControlWebSocket(
    socket,
    route,
    relayCrypto,
    journal,
    lifecycle,
    acquireActionGrant,
  ).asWebSocket();
}

const makeRelayWsRpcProtocolClientBase = RpcClient.make(RelayWsRpcGroup);

async function webSha256Base64Url(payload: string): Promise<string> {
  const bytes = new TextEncoder().encode(payload);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
}

const webRelayCrypto: RelayCrypto = {
  randomUUID: () => crypto.randomUUID(),
  sha256Base64Url: webSha256Base64Url,
};

const RELAY_ROUTE_BINDING_FRAGMENT_PREFIX = "forge-relay-binding=";
const RELAY_ROUTE_BINDING_TTL_MS = 30_000;

function relayRouteBindingId(url: string | URL): string {
  const fragment = new URL(String(url)).hash.slice(1);
  if (!fragment.startsWith(RELAY_ROUTE_BINDING_FRAGMENT_PREFIX)) {
    throw new Error("Forge relay socket URL is missing its route binding.");
  }
  const bindingId = decodeURIComponent(fragment.slice(RELAY_ROUTE_BINDING_FRAGMENT_PREFIX.length));
  if (bindingId.length === 0) {
    throw new Error("Forge relay socket URL has an invalid route binding.");
  }
  return bindingId;
}

export function createRelayRouteSocketBinding(options: {
  readonly acquireRoute: () => Promise<RelayControlRoute>;
  readonly acquireActionGrant?: (request: RelayActionGrantRequest) => Promise<string>;
  readonly crypto?: RelayCrypto;
  readonly bindingTtlMs?: number;
  readonly createSocket?: (url: string | URL, protocols?: string | string[]) => WebSocket;
  readonly journal?: RelayControlJournal;
  readonly lifecycle?: RelayConnectionLifecycle;
}): RelayRouteSocketBinding {
  const relayCrypto = options.crypto ?? webRelayCrypto;
  const bindingTtlMs = options.bindingTtlMs ?? RELAY_ROUTE_BINDING_TTL_MS;
  const createSocket =
    options.createSocket ??
    ((url: string | URL, protocols?: string | string[]) =>
      new globalThis.WebSocket(url, protocols));
  const pendingRoutes = new Map<string, PendingRelayRoute>();

  return {
    acquireSocketUrl: async () => {
      const route = await options.acquireRoute();
      const bindingId = relayCrypto.randomUUID();
      if (pendingRoutes.has(bindingId)) {
        throw new Error("Forge relay route binding identifier was reused.");
      }
      const socketUrl = new URL(route.relayWebSocketUrl);
      socketUrl.hash = `${RELAY_ROUTE_BINDING_FRAGMENT_PREFIX}${encodeURIComponent(bindingId)}`;
      const expires = setTimeout(() => pendingRoutes.delete(bindingId), bindingTtlMs);
      pendingRoutes.set(bindingId, { route, expires });
      return socketUrl.toString();
    },
    constructSocket: (url, protocols) => {
      const bindingId = relayRouteBindingId(url);
      const pendingRoute = pendingRoutes.get(bindingId);
      if (pendingRoute === undefined) {
        throw new Error("Forge relay route binding is missing, expired, or already consumed.");
      }
      pendingRoutes.delete(bindingId);
      clearTimeout(pendingRoute.expires);
      options.lifecycle?.onConnecting?.();
      return createRelayControlWebSocket(
        createSocket(pendingRoute.route.relayWebSocketUrl, protocols),
        pendingRoute.route,
        relayCrypto,
        options.journal,
        options.lifecycle,
        options.acquireActionGrant,
      );
    },
  };
}

/**
 * The payload a Forge runtime will hash, not the payload on the wire.
 *
 * The runtime's delivery middleware decodes the request with its schema and
 * then hashes the decoded value, so the declared hash has to cover that value.
 * The phone encodes with the vendored t3 contracts, which carry keys Forge's
 * schema does not (`instanceId` on every model selection, for one), and every
 * such key is dropped on decode. Hashing the wire payload therefore refused
 * every thread start from a phone with RELAY_COMMAND_PAYLOAD_HASH_MISMATCH.
 * Decoding here with the same schema the runtime uses closes the whole class.
 * A payload the schema rejects is hashed as sent, which is what happened
 * before and lets the runtime report the real decode error.
 */
const runtimePayloadDecoders = new Map<string, (payload: unknown) => unknown>();

function runtimePayloadDecoder(method: string): ((payload: unknown) => unknown) | null {
  const cached = runtimePayloadDecoders.get(method);
  if (cached !== undefined) return cached;
  const rpc = RelayWsRpcGroup.requests.get(method);
  if (rpc === undefined) return null;
  const decoder = Schema.decodeUnknownSync(rpc.payloadSchema as Schema.Codec<unknown, unknown>);
  runtimePayloadDecoders.set(method, decoder);
  return decoder;
}

export function payloadAsRuntimeDecodes(method: string, payload: unknown): unknown {
  const decode = runtimePayloadDecoder(method);
  if (decode === null) return payload;
  try {
    return decode(payload);
  } catch {
    return payload;
  }
}

export async function createRelayDeliveryHeaders(input: {
  readonly payload: unknown;
  readonly sequence: number;
  readonly requestId?: string;
  readonly issuedAt?: Date;
  readonly crypto?: RelayCrypto;
}): Promise<Readonly<Record<string, string>>> {
  const issuedAt = input.issuedAt ?? new Date();
  const relayCrypto = input.crypto ?? webRelayCrypto;
  return {
    [RELAY_REQUEST_HEADERS.requestId]: input.requestId ?? relayCrypto.randomUUID(),
    [RELAY_REQUEST_HEADERS.sequence]: String(input.sequence),
    [RELAY_REQUEST_HEADERS.issuedAt]: issuedAt.toISOString(),
    [RELAY_REQUEST_HEADERS.expiresAt]: new Date(issuedAt.getTime() + 120_000).toISOString(),
    [RELAY_REQUEST_HEADERS.payloadHash]: await relayCrypto.sha256Base64Url(
      canonicalRelayPayloadJson(input.payload),
    ),
  };
}

function makeRelayDeliveryClientLayer(scopes: ReadonlyArray<RelayScope>, relayCrypto: RelayCrypto) {
  return RpcMiddleware.layerClient(RelayDeliveryMiddleware, ({ rpc, request, next }) =>
    Effect.gen(function* () {
      const policy = authorizeRelayRpcRequest({
        method: rpc._tag,
        payload: request.payload,
        grantedScopes: scopes,
      });
      void policy;
      void relayCrypto;
      return yield* next(request);
    }),
  );
}

export function makeRelayWsRpcProtocolClient(
  scopes: ReadonlyArray<RelayScope>,
  relayCrypto: RelayCrypto = webRelayCrypto,
) {
  return makeRelayWsRpcProtocolClientBase.pipe(
    Effect.provide(makeRelayDeliveryClientLayer(scopes, relayCrypto)),
  );
}

export function createRelayRpcProtocolLayer(options: RelayRpcProtocolOptions) {
  const relayCrypto = options.crypto ?? webRelayCrypto;
  const routeSocketBinding = createRelayRouteSocketBinding({
    acquireRoute: options.acquireRoute,
    ...(options.acquireActionGrant ? { acquireActionGrant: options.acquireActionGrant } : {}),
    crypto: relayCrypto,
    journal: options.journal,
    ...(options.lifecycle ? { lifecycle: options.lifecycle } : {}),
  });
  const routeUrl = Effect.promise(routeSocketBinding.acquireSocketUrl).pipe(Effect.orDie);
  const constructorLayer = Layer.succeed(
    Socket.WebSocketConstructor,
    routeSocketBinding.constructSocket,
  );
  const socketLayer = Socket.layerWebSocket(routeUrl).pipe(Layer.provide(constructorLayer));
  const delayForRetry = options.delayForRetry ?? getWsReconnectDelayMsForRetry;
  const retryPolicy = Schedule.addDelay(
    Schedule.recurs(options.maxRetries ?? WS_RECONNECT_MAX_RETRIES),
    (retryCount) => Effect.succeed(Duration.millis(delayForRetry(retryCount) ?? 0)),
  );
  const protocolLayer = Layer.effect(
    RpcClient.Protocol,
    RpcClient.makeProtocolSocket({ retryPolicy, retryTransientErrors: true }),
  );
  return protocolLayer.pipe(Layer.provide(Layer.mergeAll(socketLayer, RpcSerialization.layerJson)));
}
